import { Text, XStack, YStack } from 'tamagui';
import { useThemeColors, Icon, dropShadow } from '@kiips/ui';
import { colors } from '../../theme/colors';
import type { PhotoAttribution } from '@my-fullstack-app/itinerary-types';
import { HeroSection } from './HeroSection';
import { PhotoAttributionCaption } from './PhotoAttributionCaption';

interface HeroStripProps {
  /** Hero image URL — Unsplash or any HTTP source. */
  image: string;
  /** Optional small label rendered above the title (e.g. "Brazil"). */
  eyebrow?: string | undefined;
  /** Big title rendered over the image (e.g. "Rio de Janeiro"). */
  title: string;
  /** Optional right-side rating (e.g. "5.0"). */
  rating?: number | undefined;
  /** Optional right-side reviews count. */
  reviewCount?: number | undefined;
  /** Optional subtitle below title (e.g. date range). */
  subtitle?: string | undefined;
  /**
   * Icon button(s) placed in the bar's right slot, before the hamburger.
   * Defaults to a heart-outline favourite button when omitted.
   */
  rightAction?: React.ReactNode | undefined;
  /** Forwarded to AppTopBar — render a back arrow to the left of the logo. */
  showBack?: boolean | undefined;
  /** Render the default favourite (heart) button when no `rightAction` is
   *  supplied. Set false on screens with nothing to favourite (e.g. the
   *  add-to-day screen, which adds a place rather than saving one).
   *  Defaults to true. */
  showFavorite?: boolean | undefined;
  /** Render a day-info column on the right of the title card (mirrors the
   *  trip-detail header). All four fields are required to enable it. */
  dayInfo?: {
    dayNumber: number;
    totalDays: number;
    label: string;
    date: string;
  } | undefined;
  /** Author attribution for the hero photo (Google Maps Platform ToS
   *  §3.2.3). Rendered as a small caption overlay on the bottom-left of
   *  the image. Omitted when the image is not a place photo (Unsplash
   *  trip covers, custom uploads). */
  photoAttribution?: PhotoAttribution | null | undefined;
  /** Show "Powered by Google" mark next to the attribution. Required
   *  when the hero photo + nearby data are sourced from Google Places
   *  (place / stay / journey screens). */
  poweredByGoogle?: boolean | undefined;
}

/**
 * Top "strip" used by every new-design secondary screen. The unified
 * AppTopBar (kiips logo + rightAction + hamburger) is embedded inside
 * the image so the bar sits within the image boundary. No separate back
 * button — users navigate back via the browser/swipe gesture or by
 * tapping the logo to go home.
 */
export function HeroStrip({
  image,
  eyebrow,
  title,
  rating,
  reviewCount,
  subtitle,
  rightAction,
  showBack,
  showFavorite = true,
  dayInfo,
  photoAttribution,
  poweredByGoogle,
}: HeroStripProps) {
  const theme = useThemeColors();

  const defaultRight = (
    <YStack
      width={40}
      height={40}
      borderRadius={20}
      backgroundColor="$surface"
      alignItems="center"
      justifyContent="center"
      {...dropShadow({ offsetY: 2, radius: 6, opacity: 0.08, color: theme.shadowDefault })}
    >
      <Icon name="favorite-border" size={20} color={theme.textOnSurface} />
    </YStack>
  );

  return (
    <YStack>
      {/* HeroSection owns the image + AppTopBar plumbing — same primitive
          used by /profile and any future hero screen. HeroStrip layers
          its title card on top (negative margin pulls it up into the
          image) so the shared chrome stays in one place. */}
      <HeroSection
        image={image}
        imageLabel={title}
        aspectRatio={1.4}
        largeHeight={520}
        showBack={showBack}
        rightSlot={rightAction ?? (showFavorite ? defaultRight : undefined)}
      >
        {photoAttribution || poweredByGoogle ? (
          // Bottom-left caption: photo credit (ToS §3.2.3) + Google
          // sourcing mark. Positioned above the title card's negative
          // margin so it stays inside the image area.
          <YStack
            position="absolute"
            left={12}
            bottom={36}
            gap={4}
            alignItems="flex-start"
          >
            <PhotoAttributionCaption attribution={photoAttribution ?? null} />
            {poweredByGoogle ? (
              <XStack
                paddingHorizontal={8}
                paddingVertical={3}
                borderRadius={6}
                backgroundColor={colors.overlayScrim}
              >
                <Text fontSize={10} color={colors.textOnDarkMuted} lineHeight={12}>
                  Powered by Google
                </Text>
              </XStack>
            ) : null}
          </YStack>
        ) : null}
      </HeroSection>
      <XStack
        backgroundColor="$surface"
        marginTop={-28}
        marginHorizontal={12}
        borderRadius={24}
        paddingVertical={14}
        paddingHorizontal={16}
        alignItems="center"
        gap={12}
        {...dropShadow({ offsetY: 4, radius: 16, opacity: 0.04, color: theme.shadowDefault })}
      >
        <YStack flex={1} minWidth={0}>
          {eyebrow ? (
            <Text fontSize={11} color="$textSubdued" fontWeight="700" letterSpacing={0.4} numberOfLines={1}>{eyebrow}</Text>
          ) : null}
          <Text fontSize={19} color="$textOnSurface" fontWeight="800" lineHeight={22} numberOfLines={2} marginTop={2}>
            {title}
          </Text>
          {subtitle ? (
            <Text fontSize={12} color="$textSubdued" marginTop={4} numberOfLines={1}>{subtitle}</Text>
          ) : null}
        </YStack>
        {dayInfo ? (
          <YStack alignItems="flex-end" minWidth={0}>
            <Text fontSize={10} color="$textSubdued" fontWeight="700" letterSpacing={0.6}>
              DAY {dayInfo.dayNumber} OF {dayInfo.totalDays}
            </Text>
            <Text fontSize={13} color="$textOnSurface" fontWeight="700" marginTop={2} numberOfLines={1}>
              {dayInfo.label}
            </Text>
            <Text fontSize={11} color="$textSubdued" marginTop={2}>
              {dayInfo.date}
            </Text>
          </YStack>
        ) : rating != null ? (
          <YStack alignItems="flex-end" gap={4}>
            <XStack
              alignItems="center"
              gap={4}
              paddingHorizontal={10}
              paddingVertical={4}
              borderRadius={12}
              borderWidth={1}
              borderColor="$borderLight"
            >
              <Icon name="star" size={14} color={theme.textOnSurface} />
              <Text fontSize={13} fontWeight="700" color="$textOnSurface">{rating.toFixed(1)}</Text>
            </XStack>
            {reviewCount != null ? (
              <Text fontSize={12} color="$textOnSurface" textDecorationLine="underline">
                {reviewCount} reviews
              </Text>
            ) : null}
          </YStack>
        ) : null}
      </XStack>
    </YStack>
  );
}
